import omit from 'lodash/omit';
import type { ChannelListItem } from '@/models';

/**
 * What the line item form is doing with the line it was opened with.
 *
 * - `create`    — a blank new line.
 * - `edit`      — saving over an existing line.
 * - `duplicate` — populated from an existing line, but saved as a new one.
 *
 * Decide this once, with {@link getLineItemFormMode}, rather than from `!!_id`
 * at each call site: a duplicate carries the source line's `_id` so the form
 * can populate from it, which makes `_id` alone answer "is this an edit?"
 * wrongly.
 */
export type LineItemFormMode = 'create' | 'edit' | 'duplicate';

/**
 * What a line has actually delivered. Never carried onto a copy — it would
 * report delivery against a booking that has not run.
 */
const DELIVERY_FIELDS = ['impressions', 'clicks', 'views', 'listens'] as const;

/**
 * Resolves the mode for the line the form was opened with.
 *
 * @param line - The line passed to the form; `undefined` when adding a new one.
 * @returns `create` with no saved line, `duplicate` when the line is a copy made
 *   by {@link duplicateLineItem}, otherwise `edit`.
 */
export const getLineItemFormMode = (line?: ChannelListItem): LineItemFormMode => {
  if (!line?._id) return 'create';
  return line.duplicatedFrom ? 'duplicate' : 'edit';
};

/**
 * Whether the form opened on an existing line's values (edit or duplicate).
 *
 * While true, the form populates from that line and auto-fill — rate source
 * defaults, package-derived fields, dependency resets — must not overwrite it.
 *
 * @param mode - The form's mode, from {@link getLineItemFormMode}.
 */
export const isPrefilledMode = (mode: LineItemFormMode): boolean => mode !== 'create';

/**
 * Builds a copy of a line item for the form to open as a new line.
 *
 * `_id` is kept so the form populates every field from it; `duplicatedFrom`
 * is what makes {@link getLineItemFormMode} treat it as a create, so saving
 * never overwrites the source line.
 *
 * What the line was *booked* as — channel, rate, dates, targeting, formats,
 * package — is kept; what it has *delivered* ({@link DELIVERY_FIELDS}) is not.
 *
 * @param line - The saved line to copy. It is not mutated.
 * @returns A new line item marked as a duplicate of `line`.
 */
export const duplicateLineItem = (line: ChannelListItem): ChannelListItem => ({
  ...omit(line, DELIVERY_FIELDS),
  duplicatedFrom: line._id
});
